import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FileType } from '@Components/FileUploadDropzone/FileTypes';
import FilesApi from '@Data/files/Api';
import {
    FileUploadStep,
    IFileEntity,
    IFileUploadHeader,
} from '@Data/files/Interfaces';
import {
    fileUploadCurrentStep,
    fileUploadFileIdField,
    fileWorkflowEngineState,
} from '@Data/files/Reducer';
import {
    UploadFileWorkflowAction,
    UploadFileWorkflowState,
} from '@Data/files/UploadFileWorkflow';
import NotificationsApi from '@Data/notifications/Api';
import {
    NotificationSeverity,
    NotificationType,
} from '@Data/notifications/Interfaces';
import SelectHeadersStep from './SelectHeadersStep';
import UploadFileStep from './UploadFileStep';

const FileUploadDialogue = (props) => {
    const { projectId, onCancel, setCancelDisabled, onComplete, setFooter } =
        props;

    const [selectedFiles, setSelectedFiles] = useState<Array<File>>(null);
    const [newFolderName, setNewFolderName] = useState<string>('');
    const [isSubmittingFile, setIsSubmittingFile] = useState<boolean>(false);
    const [isSubmittingFields, setIsSubmittingFields] =
        useState<boolean>(false);
    const fileId: string = useSelector(fileUploadFileIdField);
    const workflowEngineState: string | null = useSelector(
        fileWorkflowEngineState
    );

    useEffect(() => {
        if (!workflowEngineState) {
            FilesApi.getNewWorkflowEngineState(UploadFileWorkflowState.PENDING);
        }
    }, [workflowEngineState]);

    const handleSelectFile = (files: Array<File>, fileType: FileType) => {
        setSelectedFiles(files);

        FilesApi.getNewWorkflowEngineState(
            workflowEngineState,
            UploadFileWorkflowAction.SELECTED_FILES,
            fileType
        );
    };

    const handleSelectFileError = () => {
        console.log('err');
        setSelectedFiles([]);
        FilesApi.getNewWorkflowEngineState(
            workflowEngineState,
            UploadFileWorkflowAction.SELECTED_FILES_ERROR
        );
    };

    const handleSubmitFiles = () => {
        switch (workflowEngineState) {
            case UploadFileWorkflowState.SUBMITTING_HEADERS:
                return handleUploadCsv();
            case UploadFileWorkflowState.CREATING_FOLDER:
                return createFolderAndUploadFiles();
        }
    };

    const handleUploadCsv = () => {
        const onSuccess = () => {
            FilesApi.trySetLatLng(() => {
                setCancelDisabled(false);
            });
        };

        setIsSubmittingFile(true);
        setCancelDisabled(true);

        FilesApi.uploadCsv(selectedFiles[0], projectId, onSuccess, resetState);
    };

    const createFolderAndUploadFiles = () => {
        setCancelDisabled(true);
        setIsSubmittingFile(true);

        FilesApi.createFolderAndUploadFiles(selectedFiles, projectId, () => {
            resetState();
            onComplete();
        });
    };

    const handleSubmitHeaders = (
        latField: IFileUploadHeader,
        lngField: IFileUploadHeader,
        poleIdField: IFileUploadHeader
    ) => {
        setIsSubmittingFields(true);
        setCancelDisabled(true);

        const handleSuccess = (taskId: string) => {
            NotificationsApi.addNotification(
                `${newFolderName} file upload process`,
                NotificationSeverity.INFO,
                NotificationType.PROGRESS,
                taskId
            );
            resetState();
            onComplete();
        };

        const handleFailure = () => {
            setIsSubmittingFields(false);
            setCancelDisabled(false);
        };

        FilesApi.submitHeaders(
            latField,
            lngField,
            poleIdField,
            fileId,
            newFolderName,
            handleSuccess,
            handleFailure
        );
    };

    const resetState = () => {
        FilesApi.resetState(() => {
            setIsSubmittingFile(false);
            setSelectedFiles(null);
            setNewFolderName('');
        });
    };

    switch (workflowEngineState) {
        case UploadFileWorkflowState.SELECTING_HEADERS:
            return (
                <SelectHeadersStep
                    onBack={resetState}
                    onSubmit={handleSubmitHeaders}
                    isSubmitting={isSubmittingFields}
                    setFooter={setFooter}
                />
            );
        default:
            return (
                <UploadFileStep
                    onSelectFile={handleSelectFile}
                    onSelectFileError={handleSelectFileError}
                    canSubmitFile={[
                        UploadFileWorkflowState.CREATING_FOLDER,
                        UploadFileWorkflowState.SUBMITTING_HEADERS,
                    ].includes(workflowEngineState)}
                    onCancel={onCancel}
                    isSubmitting={isSubmittingFile}
                    onSubmit={handleSubmitFiles}
                    newFolderName={newFolderName}
                    editFolderName={setNewFolderName}
                    setFooter={setFooter}
                    files={selectedFiles}
                />
            );
    }
};

FileUploadDialogue.propTypes = {
    projectId: PropTypes.number,
    onCancel: PropTypes.func,
    setCancelDisabled: PropTypes.func,
    onComplete: PropTypes.func,
    setFooter: PropTypes.func,
};
export default FileUploadDialogue;
